/**
 * MooringRopeRenderer.ts — reusable visual mooring-rope renderer.
 *
 * A single instanced rope mesh group is registered on a `StaticMeshPass`,
 * and each "line" the renderer manages reserves a fixed number of segment
 * instances. Each segment is a thin cylinder along +X that gets oriented
 * + scaled per-frame to stitch a parabolic catenary between two endpoints.
 *
 * Usage:
 *   const ropes = new MooringRopeRenderer(staticMeshPass, { ... });
 *   const lineId = ropes.addLine();        // reserves ROPE_SEGMENTS slots
 *   …each frame…
 *   ropes.updateLine(lineId, worldA, worldB, restLength, waterLevel);
 *   ropes.hideLine(lineId);                // when slack/inactive
 *
 * The renderer is rendering-only: physics (spring/damper) lives on
 * `MooringLine`, which provides the world-space endpoints + tension state.
 */

import type { StaticMeshPass, Mat4 } from "../passes/StaticMeshPass";
import { createCylinderX } from "../assets/ProceduralAssets";

export interface MooringRopeRendererOptions {
  /** Mesh group id (must be unique within the StaticMeshPass). */
  meshId?: string;
  /** Number of cylinder segments per line. Default 16. */
  segmentsPerLine?: number;
  /** Cylinder radius (m). Default 0.035. */
  radius?: number;
  /** Mesh resolution around the cylinder. Default 8. */
  radial?: number;
  /** Albedo of the rope. Default sun-bleached hemp. */
  color?: [number, number, number];
  /**
   * Sag gain — fraction of slack length that becomes vertical sag at the
   * line midpoint. 0 = always straight, 1 = full slack collapses to a deep
   * U. Default 0.7.
   */
  sagGain?: number;
}

export interface MooringRopeUpdateOptions {
  /** Clamp slack sag above the water surface. Default true for mooring lines. */
  clampToWater?: boolean;
}

export class MooringRopeRenderer {
  readonly meshId: string;
  readonly segmentsPerLine: number;
  private readonly sagGain: number;
  private readonly pass: StaticMeshPass;
  private readonly matrices: Mat4[] = [];
  private lineCount = 0;

  constructor(pass: StaticMeshPass, opts: MooringRopeRendererOptions = {}) {
    this.pass = pass;
    this.meshId = opts.meshId ?? "mooring_rope";
    this.segmentsPerLine = opts.segmentsPerLine ?? 16;
    this.sagGain = opts.sagGain ?? 0.7;
    pass.addGroup(
      this.meshId,
      createCylinderX(
        opts.radius ?? 0.035,
        opts.radial ?? 8,
        opts.color ?? [0.92, 0.85, 0.55],
        this.meshId,
      ),
    );
  }

  /** Reserve a new line and return its handle (start index of segments). */
  addLine(): number {
    const start = this.lineCount * this.segmentsPerLine;
    for (let s = 0; s < this.segmentsPerLine; s++) {
      // prettier-ignore
      const m = new Float32Array([
        0, 0, 0, 0,
        0, 0, 0, 0,
        0, 0, 0, 0,
        0, 0, 0, 1,
      ]) as Mat4;
      this.matrices.push(m);
      this.pass.addInstance(this.meshId, m);
    }
    this.lineCount++;
    return start;
  }

  /** Hide all segments of a line (e.g. when the line is slack/inactive). */
  hideLine(handle: number): void {
    for (let s = 0; s < this.segmentsPerLine; s++) {
      this.hideSegment(this.matrices[handle + s]!);
    }
  }

  /**
   * Update one line by computing a parabolic-sag chain between `a` and `b`.
   * `restLength` defines the unstretched rope length — when distance ≥
   * restLength the rope renders straight (taut). `waterLevel` clamps the
   * sag so the rope's lowest point doesn't dip below the water surface.
   */
  updateLine(
    handle: number,
    a: readonly [number, number, number],
    b: readonly [number, number, number],
    restLength: number,
    waterLevel: number,
    opts: MooringRopeUpdateOptions = {},
  ): void {
    const N = this.segmentsPerLine;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const dz = b[2] - a[2];
    const dist = Math.hypot(dx, dy, dz);
    if (dist < 1e-4) {
      this.hideLine(handle);
      return;
    }
    const slack = Math.max(0, restLength - dist);
    let sag = slack * this.sagGain;
    if (opts.clampToWater ?? true) {
      const minEnd = Math.min(a[1], b[1]);
      const maxSag = Math.max(0, minEnd - waterLevel - 0.05);
      if (sag > maxSag) sag = maxSag;
    }
    let prevX = a[0];
    let prevY = a[1];
    let prevZ = a[2];
    for (let s = 0; s < N; s++) {
      const t1 = (s + 1) / N;
      const lx = a[0] + dx * t1;
      const ly = a[1] + dy * t1;
      const lz = a[2] + dz * t1;
      const drop = 4 * sag * t1 * (1 - t1);
      const qx = lx;
      const qy = ly - drop;
      const qz = lz;
      this.setSegmentMatrix(
        this.matrices[handle + s]!,
        prevX,
        prevY,
        prevZ,
        qx,
        qy,
        qz,
      );
      prevX = qx;
      prevY = qy;
      prevZ = qz;
    }
  }

  /** Update one line as a piecewise-linear rope through explicit world points. */
  updatePolyline(
    handle: number,
    points: readonly (readonly [number, number, number])[],
  ): void {
    const N = this.segmentsPerLine;
    if (points.length < 2) {
      this.hideLine(handle);
      return;
    }

    const lengths: number[] = [];
    let total = 0;
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i]!;
      const b = points[i + 1]!;
      const d = Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
      lengths.push(d);
      total += d;
    }
    if (total < 1e-4) {
      this.hideLine(handle);
      return;
    }

    const sample = (distance: number): [number, number, number] => {
      let remaining = Math.max(0, Math.min(total, distance));
      for (let i = 0; i < lengths.length; i++) {
        const segLen = lengths[i]!;
        const a = points[i]!;
        const b = points[i + 1]!;
        if (remaining <= segLen || i === lengths.length - 1) {
          const t = segLen > 1e-4 ? remaining / segLen : 0;
          return [
            a[0] + (b[0] - a[0]) * t,
            a[1] + (b[1] - a[1]) * t,
            a[2] + (b[2] - a[2]) * t,
          ];
        }
        remaining -= segLen;
      }
      const last = points[points.length - 1]!;
      return [last[0], last[1], last[2]];
    };

    let prev = sample(0);
    for (let s = 0; s < N; s++) {
      const next = sample(((s + 1) / N) * total);
      this.setSegmentMatrix(
        this.matrices[handle + s]!,
        prev[0],
        prev[1],
        prev[2],
        next[0],
        next[1],
        next[2],
      );
      prev = next;
    }
  }

  // --- internals ---------------------------------------------------------

  private hideSegment(m: Mat4): void {
    for (let k = 0; k < 16; k++) m[k] = 0;
    m[15] = 1;
  }

  /** Orient a unit cylinder (along +X, length 1) from p→q into matrix m. */
  private setSegmentMatrix(
    m: Mat4,
    px: number,
    py: number,
    pz: number,
    qx: number,
    qy: number,
    qz: number,
  ): void {
    const dx = qx - px;
    const dy = qy - py;
    const dz = qz - pz;
    const L = Math.hypot(dx, dy, dz);
    if (L < 1e-4) {
      this.hideSegment(m);
      return;
    }
    const ix = dx / L,
      iy = dy / L,
      iz = dz / L;
    let jx = 0,
      jy = 1,
      jz = 0;
    const proj = jx * ix + jy * iy + jz * iz;
    jx -= ix * proj;
    jy -= iy * proj;
    jz -= iz * proj;
    let jl = Math.hypot(jx, jy, jz);
    if (jl < 1e-3) {
      jx = 0;
      jy = 0;
      jz = 1;
      const proj2 = jx * ix + jy * iy + jz * iz;
      jx -= ix * proj2;
      jy -= iy * proj2;
      jz -= iz * proj2;
      jl = Math.hypot(jx, jy, jz);
    }
    jx /= jl;
    jy /= jl;
    jz /= jl;
    const kx = iy * jz - iz * jy;
    const ky = iz * jx - ix * jz;
    const kz = ix * jy - iy * jx;
    const mx = (px + qx) * 0.5;
    const my = (py + qy) * 0.5;
    const mz = (pz + qz) * 0.5;
    m[0] = ix * L;
    m[1] = iy * L;
    m[2] = iz * L;
    m[3] = 0;
    m[4] = jx;
    m[5] = jy;
    m[6] = jz;
    m[7] = 0;
    m[8] = kx;
    m[9] = ky;
    m[10] = kz;
    m[11] = 0;
    m[12] = mx;
    m[13] = my;
    m[14] = mz;
    m[15] = 1;
  }
}
